import { log } from '@ms-ows/logging'

import { TargetElementInfo } from './models'

export type CreateDivElementOptions = Partial<{
  attributes: Record<string, string>
  classNames: string[]
  id: string
  parentElement: HTMLElement | Document
}>

export const createDivElement = ({
  attributes = {},
  classNames = [],
  id = '',
  parentElement = document.body || document,
}: CreateDivElementOptions) => {
  const newElement = document.createElement('div')

  Object.keys(attributes).forEach(a =>
    newElement.setAttribute(a, attributes[a])
  )

  classNames.forEach(c => newElement.classList.add(c))

  if (id) {
    newElement.id = id
  }

  parentElement.appendChild(newElement)

  return newElement
}

export const getOrCreateSubElement = (id: string, rootContainer: HTMLElement) =>
  document.getElementById(id) ||
  createDivElement({ id, parentElement: rootContainer })

export const getTargetElementInfo = (selectors: string[]) => {
  if (selectors) {
    for (const selector of selectors) {
      try {
        const targetElement = document.querySelector<HTMLElement>(selector)
        if (targetElement) {
          return { selector, targetElement }
        }
      } catch (error) {
        log.local('query selector error', error as unknown)
      }
    }
  }
}

/**
 * wrapper around getTargetElementInfo that allows caller to demand a
 * result with at least the first selector in it
 */
export const getTargetElementInfoWithFallback = (
  selectors: string[]
): Partial<TargetElementInfo> =>
  getTargetElementInfo(selectors) || {
    selector: selectors?.[0],
  }

export const removeElement = (element: HTMLElement) => {
  if (element.parentElement) {
    element.parentElement.removeChild(element)
  } else if (element.remove) {
    element.remove()
  }
}

/**
 * Gets the computed style for a hypothetical element matching a selector.
 * Support is limited to classnames and children
 */
export const getColorForSelector = (selector: string) => {
  const [root, leaf] = selector
    .split(' ')
    .filter(s => s.startsWith('.'))
    .reduce(([r, l], s) => {
      const element = createDivElement({
        classNames: s.split('.').filter(c => c),
        parentElement: l,
      })

      return [r || element, element]
    }, [] as HTMLElement[])

  // need to grab color while still in the dom
  const { color } = getComputedStyle(leaf)

  removeElement(root)

  return color
}

export const addEventHandler = (
  target: HTMLElement | Window,
  name: keyof HTMLElementEventMap,
  onEvent: () => void
) => {
  const eventHandler = () => {
    onEvent()
    target.removeEventListener(name, eventHandler)
  }

  target.addEventListener(name, eventHandler)

  return () => target.removeEventListener(name, eventHandler)
}

export const addElementClickHandler = (
  targetElement: HTMLElement,
  onClick: () => void
) => addEventHandler(targetElement, 'click', onClick)

export const addResizeHandler = (onResize: () => void) =>
  addEventHandler(window, 'resize', onResize)

export const checkIntersection = (
  target: HTMLElement,
  element: HTMLElement
) => {
  const targetRect = target.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()

  return !(
    elementRect.right < targetRect.left ||
    elementRect.left > targetRect.right ||
    elementRect.top > targetRect.bottom ||
    elementRect.bottom < targetRect.top
  )
}

export const watchForElementHidden = (
  element: HTMLElement,
  onHidden: () => void,
  offscreenIsHidden = true
) => {
  if (offscreenIsHidden) {
    const viewportObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => !entry.isIntersecting)) {
        onHidden()
        viewportObserver.disconnect()
      }
    })

    viewportObserver.observe(element)

    return () => viewportObserver.disconnect()
  }

  const mutationObserver = new MutationObserver(mutations => {
    mutations.forEach(m => {
      if (
        (m.type === 'childList' &&
          Array.prototype.slice.call(m.removedNodes).filter(e => e === element)
            .length) ||
        (m.type === 'attributes' &&
          m.target === element &&
          (element.style.display === 'none' ||
            element.style.visibility === 'hidden'))
      ) {
        onHidden()
        mutationObserver.disconnect()
      }
    })
  })

  if (element.parentElement) {
    mutationObserver.observe(element.parentElement, {
      attributeFilter: ['style'],
      childList: true,
      subtree: true,
    })
  } else {
    mutationObserver.observe(element, { attributeFilter: ['style'] })
  }

  return () => mutationObserver.disconnect()
}
